import React, { Component } from "reactn";
import TagsInput from "react-tagsinput";
import { Chip, Popover } from "@material-ui/core";
import styled from "styled-components";
import Icon from "js/react/components/Icon";
import { delimiterRegex } from "common/regex";
import { TAG_DELIMITERS } from "js/react/constants";
import { InviteSuggestions } from "./InviteSuggestions";
import { Key } from "js/core/utilities/keys";
import { clipboardRead, ClipboardType } from "js/core/utilities/clipboard";
import { t } from "js/i18n";
import { _ } from "js/vendor";

const ERROR_COLOR = "#FB0000";
const ERROR_MESSAGE_COLOR = "#FF4500";

const InviteTagsRoot = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    width: 100%;
`;

const InviteTagChip = styled(Chip)`
    &&& {
        background: #F1F1F1;
        color: ${props => props.$hasError ? ERROR_MESSAGE_COLOR : "#666"};
        font-family: Source Sans Pro;
        font-size: 13px;
        font-weight: 600;
        line-height: normal;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 2px 10px;
        border-radius: 25px;
        height: auto;
        overflow: visible;

        .MuiChip-label {
            padding: 0;
            overflow: visible;
            line-height: normal;
            color: ${props => props.$hasError ? ERROR_MESSAGE_COLOR : "#666"};
        }

        .MuiChip-deleteIcon {
            width: 18px;
            height: 18px;
            margin: 0;
            color: #666;
            font-size: 18px;
        }
    }
`;

const BUTTON_HEIGHT = 36;

const StyledTagsInput = styled(TagsInput)`
    box-sizing: border-box;
    flex-grow: 2;
    min-height: ${BUTTON_HEIGHT}px;
    padding: 5px 8px;
    background-color: #fff;
    border: 1px solid ${props => props.$hasError ? ERROR_COLOR : "#ccc"};
    overflow: hidden;
    font-size: 12px;
`;

const StyledInput = styled.input`
    background: transparent;
    border: 0;
    color: ${props => props.$hasError ? ERROR_COLOR : "#222"};
    font-family: Source Sans Pro;
    font-weight: 500;
    outline: none;
    margin: 0;
    padding: 0 5px;
    font-size: 14px;
    line-height: 24px;
    height: 24px;
    flex-grow: 2;

    &::placeholder {
        color: #999;
    }
`;

const TagLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
`;

const Error = styled.div.attrs({ "data-test-id": "invite-tags-error" })`
    display: flex;
    width: 100%;
    color: ${ERROR_MESSAGE_COLOR};
    font-size: 14px;
    font-weight: 400;
    align-items: center;
    padding: 6px 0 0;
`;

const InviteTag = props => {
    const {
        inviteContext,
        tag,
        keyProp,
    } = props;

    const label = tag.displayName || tag.name || tag.email;
    return (
        <InviteTagChip
            key={keyProp}
            label={label}
            $hasError={!!tag.inviteError}
            onDelete={() => inviteContext.deselectItems([tag])}
            deleteIcon={<Icon small iconName="close" />}
        />
    );
};

function getInviteFieldErrors(inviteContext) {
    const errors = [];
    const selection = inviteContext.selection || [];

    if (selection.some(tag => tag.inviteError === "alreadyHasAccess")) {
        errors.push(t("Member already has access"));
    }
    if (selection.some(tag => tag.inviteError === "duplicate")) {
        errors.push(t("Duplicate"));
    }

    if (inviteContext.errors?.length) {
        errors.push(...inviteContext.errors);
    }

    return _.uniq(errors);
}

export class InviteTagsInput extends Component {
    constructor(props) {
        super(props);

        this.state = {
            permission: "write",
            duplicatedEmails: [],
            suggestionsAllowedOpen: false,
            skipNextFocus: false,
        };

        if (this.props.tags?.length) {
            this.handleChangeTags(this.props.tags);
        }

        this.refInput = React.createRef();
    }

    get suggestionsOpen() {
        const {
            inviteContext,
        } = this.props;
        const {
            suggestionsAllowedOpen,
        } = this.state;

        const hasNoMatches = inviteContext.errors?.includes(t("No matches"));

        const result = (
            suggestionsAllowedOpen &&
            (
                // Suggestions are only allowed open if the
                //   selection is empty or the filter is non-empty
                !inviteContext.selection.length ||
                !!inviteContext.filter.length
            ) &&
            (
                // Only show the suggestions if we have suggestions to show
                //   or the filter starts with a category tag
                !!inviteContext.filteredAllSuggestions.length ||
                inviteContext.filter.startsWith("@") ||
                inviteContext.filter.startsWith("#")
            ) &&
            // Don't show suggestions if we've got a visible error
            !inviteContext.errors?.length &&
            !hasNoMatches
        );
        return result;
    }

    inputScrollIntoView = () => {
        // Let UI have a frame to update before we try to scroll
        setTimeout(() => {
            this.refInput.current?.scrollIntoView({
                block: "end",
                inline: "end",
                behavior: "smooth",
            });
        });
    }

    handleChangeTags = (tags, changed) => {
        const { inviteContext } = this.props;
        inviteContext.selectItems(changed);
    }

    handleKeyCodes = event => {
        const { inviteContext } = this.props;

        switch (event.keyCode) {
            case Key.BACKSPACE:
                if (!inviteContext.filter) {
                    // Prevent further events
                    event.nativeEvent.stopImmediatePropagation();
                    event.nativeEvent.preventDefault();

                    inviteContext.popItem();
                }
                break;
            case Key.ENTER:
            case Key.TAB:
                // Only select on ENTER/TAB when the suggestions
                //   are open and have available suggestions
                if (
                    inviteContext.selectableSuggestions.length &&
                    this.suggestionsOpen
                ) {
                    // Prevent further events
                    event.nativeEvent.stopImmediatePropagation();
                    event.nativeEvent.preventDefault();

                    inviteContext.selectPendingSelection();
                } else if (inviteContext.filter.length) {
                    // Prevent further events
                    event.nativeEvent.stopImmediatePropagation();
                    event.nativeEvent.preventDefault();

                    inviteContext.selectFilterAsEmail();
                }
                break;
            case Key.SPACE:
            case Key.COMMA:
            case Key.SEMICOLON:
            case Key.UP_ARROW:
            case Key.DOWN_ARROW:
                // Prevent further events
                event.nativeEvent.stopImmediatePropagation();
                event.nativeEvent.preventDefault();

                switch (event.keyCode) {
                    case Key.SPACE:
                    case Key.COMMA:
                    case Key.SEMICOLON:
                        inviteContext.selectFilterAsEmail();
                        break;
                    case Key.UP_ARROW:
                        if (this.suggestionsOpen) {
                            inviteContext.pendingSelectPrev();
                        }
                        break;
                    case Key.DOWN_ARROW:
                        if (this.suggestionsOpen) {
                            inviteContext.pendingSelectNext();
                        }
                        break;
                }
                break;
        }

        this.inputScrollIntoView();
    }

    handleFilter = event => {
        const { inviteContext } = this.props;
        const filter = event.target.value;
        inviteContext.setFilter(filter);

        // Ensure the suggestions are open after the filter has changed
        if (!this.state.suggestionsAllowedOpen) {
            this.setState({
                suggestionsAllowedOpen: true,
            });
        }

        this.inputScrollIntoView();
    }

    handlePaste = async event => {
        const text = await clipboardRead([ClipboardType.TEXT], event);
        if (text) {
            // Prevent the default pasting in the textbox
            event.nativeEvent.stopImmediatePropagation();
            event.nativeEvent.preventDefault();
            const emails = text.split(delimiterRegex).map(d => d.trim());
            const { inviteContext } = this.props;
            inviteContext.selectItems(emails);
        }
    }

    handleFocus = event => {
        if (event.relatedTarget) {
            const { inviteContext } = this.props;

            // Only open the suggestions on focus if we don't have any selections.
            //   This is to minimize overlaying submit buttons.
            if (!inviteContext.selection.length) {
                this.setState({
                    suggestionsAllowedOpen: true,
                });
            }
        }
    }

    handleBlur = event => {
        if (event.relatedTarget) {
            this.setState({
                suggestionsAllowedOpen: false,
            });

            const { inviteContext } = this.props;

            inviteContext.selectFilterAsEmail();
        }
    }

    render() {
        const { inviteContext, inputProps, className } = this.props;
        const fieldErrors = getInviteFieldErrors(inviteContext);
        const hasFieldError = fieldErrors.length > 0;
        const hasNoMatchesError = inviteContext.errors?.includes(t("No matches"));

        return (
            <InviteTagsRoot>
                <div
                    id="invite"
                    className={`invite-input${className ? ` ${className}` : ""}`}
                >
                    <StyledTagsInput
                        className={`tags-input${className ? ` ${className}` : ""}`}
                        $hasError={hasFieldError}
                        addKeys={TAG_DELIMITERS}
                        addOnPaste
                        maxTags={50}
                        value={inviteContext.selection}
                        renderTag={props => (
                            <InviteTag
                                {...props}
                                inviteContext={inviteContext}
                                keyProp={props.key}
                            />
                        )}
                        onChange={this.handleChangeTags}
                        renderInput={props => (
                            <StyledInput
                                ref={this.refInput}
                                placeholder={inviteContext.filterPlaceholder}
                                value={inviteContext.filter}
                                $hasError={hasNoMatchesError}
                                onKeyDown={this.handleKeyCodes}
                                onChange={this.handleFilter}
                                onPaste={this.handlePaste}
                                onFocus={this.handleFocus}
                                onBlur={this.handleBlur}
                                {...inputProps}
                            />
                        )}
                        renderLayout={(tagComponents, inputComponent) => (
                            <TagLayout>
                                {tagComponents}
                                {inputComponent}
                            </TagLayout>
                        )}
                    />
                </div>
                {
                    hasFieldError &&
                    <Error>
                        <span>{fieldErrors.join(", ")}</span>
                    </Error>
                }
                <Popover
                    disableAutoFocus
                    disableEnforceFocus
                    transitionDuration={{ exit: 0 }}
                    open={this.suggestionsOpen}
                    anchorEl={this.refInput.current}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left"
                    }}
                    onClose={() => {
                        this.setState({
                            suggestionsAllowedOpen: false,
                        });
                    }}
                >
                    <InviteSuggestions
                        {...this.props}
                        onSelect={() => this.inputScrollIntoView()}
                    />
                </Popover>
            </InviteTagsRoot>
        );
    }
}
